import { z } from "zod";
/**
 * The read-only API TRY's backend calls to build Min side. This file is the contract and is
 * published to TRY verbatim, so each field says what it means here and nowhere else, and our
 * tests parse every response — success and failure — against it. Base URL, key, paging and the
 * customer id rule are in the document this file is published in. Fields not written here do
 * not exist.
 */
// ---- Conventions ------------------------------------------------------------------------------
/** `2026-04-09T16:30:00+02:00`: ISO 8601 with the Oslo offset, never `Z`, never a date alone. */
const isoDateTimeSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/, "ISO 8601 med tidssone");
/** Kroner as a JSON number with at most two decimals (`9000`, `8999.5`, `8999.75`), never øre. */
const nokAmountSchema = z.number().multipleOf(0.01);
/** The previous page's `nextCursor`, passed back exactly as received. Opaque. */
const cursorSchema = z.string().min(1);
// ---- Request ----------------------------------------------------------------------------------
/**
 * The customer's id in the Omnium web shop, the shop the customer signs in to on your side — so
 * the id is one you already hold, and no lookup of ours stands between the two. Sent in the path
 * exactly as the shop writes it, percent-encoded; surrounding spaces are trimmed before we read
 * it. An id that is nothing but spaces: 400 `VALIDATION`. Leaving the segment out is a different
 * URL and answers 404 `NOT_FOUND`. An id we hold no customer for: an empty page, never 404.
 */
export const publicCustomerParamsSchema = z.object({ customerId: z.string().min(1) });
/** Paging, the same on every list route. Both optional; neither means the first page of 100. */
export const publicListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(100).describe("Rows per page, 1–100"),
    cursor: cursorSchema.optional().describe("Omit for the first page"),
});
// ---- Booking ----------------------------------------------------------------------------------
export const publicBookingTypeSchema = z.enum(["VERDIVURDERING", "SALG", "ANALYSE"]);
export const publicBookingStatusSchema = z.enum([
    "PENDING",
    "CONFIRMED",
    "COMPLETED",
    "CANCELLED",
    "NO_SHOW",
]);
export const publicBookingSchema = z.object({
    id: z.string().describe("Stable booking id"),
    type: publicBookingTypeSchema.describe("What the appointment is for"),
    title: z.string().describe("Display title, as the store names that appointment type"),
    startTime: isoDateTimeSchema.describe("When the appointment starts"),
    durationMinutes: z.number().int().describe("Length of the appointment"),
    location: z
        .object({ id: z.string(), name: z.string() })
        .describe("The store: our id for it, and its name"),
    status: publicBookingStatusSchema.describe("Where the booking stands"),
    cancellable: z
        .boolean()
        .describe("Whether the customer may still cancel: PENDING or CONFIRMED, and startTime in the future. Cancelling itself is not in this API"),
});
// ---- Gold delivery ----------------------------------------------------------------------------
/**
 * One shape for both ways gold arrives, in store and by post; which of the two a row is stays
 * our concern. The status is decided in this order — the first that applies is the delivery's
 * status, so a paid delivery reads PAID and not ACCEPTED.
 */
export const publicDeliveryStatusSchema = z.enum([
    "PAID", // finished, and the payout or gift card is registered
    "ARCHIVED", // the delivery is closed without being paid out or settled as a gift card
    "RETURNED", // the gold has been sent back to the customer
    "ACCEPTED", // the customer accepted the offer; the payout is being prepared
    "DECLINED", // the customer declined; the gold is being handled for return
    "OFFER_SENT", // we have sent an offer for the whole delivery
    "ASSESSING", // the items are registered and a goldsmith is assessing them
    "RECEIVED", // we have recorded the received weight
    "AT_STORE", // the posted envelope has reached us
    "POUCH_ORDERED", // the envelope is on its way to the customer
    "REGISTERED", // registered in the store and waiting for handling
    "WAITING", // received and waiting to be registered
]);
export const publicOfferSchema = z.object({
    amount: nokAmountSchema.describe("Our offer for the whole delivery"),
    currency: z.literal("NOK"),
    status: z
        .enum(["PENDING", "ACCEPTED", "DECLINED"])
        .describe("PENDING until the customer answers"),
    expiresAt: isoDateTimeSchema
        .nullable()
        .describe("When the offer lapses; null when it has no deadline"),
});
/** TRY's `deliveryMethod` values as written, renamed because ours means how the gold reached us. */
export const publicMailOrderReturnMethodSchema = z.enum([
    "post_i_butikk",
    "egen_postkasse",
    "postboks",
]);
export const publicGoldDeliverySchema = z.object({
    id: z.string().describe("Stable delivery id; a payout's goldDeliveryId points at it"),
    reference: z
        .string()
        .nullable()
        .describe("The receipt code printed on the customer's receipt; null when the delivery has none"),
    createdAt: isoDateTimeSchema.describe("When the delivery was registered"),
    status: publicDeliveryStatusSchema.describe("Where the delivery stands"),
    totalWeightGrams: z
        .number()
        .nullable()
        .describe("The weight we recorded on arrival; null until the gold has been weighed"),
    items: z
        .array(z.object({ description: z.string().nullable() }))
        .describe("The items in the delivery; the item count is the length of this array"),
    offer: publicOfferSchema.nullable().describe("Our offer, once it has been sent; null before"),
    mailOrderReturnMethod: publicMailOrderReturnMethodSchema
        .nullable()
        .describe("How a posted delivery is returned. Always null today: nothing records the method yet"),
    imageUrls: z
        .array(z.string().url())
        .describe("The customer's photos, as full URLs: load with a plain <img src>; no expiry; a JS fetch would need CORS on the bucket, which is not supported. A photo whose file is gone is left out"),
    updatedAt: isoDateTimeSchema.describe("When the delivery last changed"),
});
// ---- Payout -----------------------------------------------------------------------------------
export const publicPayoutStatusSchema = z.enum([
    "PENDING",
    "ACCEPTED",
    "WAITING_FOR_PAYOUT",
    "SENT_TO_FINAGO",
    "PAID",
    "CANCELLED",
]);
/** A gift card is not money in the bank, and the page must not say it is. */
export const publicPayoutMethodSchema = z.enum(["BANK_TRANSFER", "GIFT_CARD"]);
export const publicPayoutSchema = z.object({
    id: z.string().describe("Stable payout id"),
    goldDeliveryId: z
        .string()
        .nullable()
        .describe("The id of the gold delivery this pays for; null when no delivery is linked"),
    amount: nokAmountSchema.describe("The amount in kroner"),
    currency: z.literal("NOK"),
    status: publicPayoutStatusSchema.describe("Where the payout stands"),
    method: publicPayoutMethodSchema.describe("How the customer is settled with"),
    payoutDate: isoDateTimeSchema
        .nullable()
        .describe("When the payout was registered as paid; null until then"),
    maskedAccount: z
        .string()
        .nullable()
        .describe("**** and the last four digits of the account; null when we hold no account, or fewer than four digits"),
});
// ---- Gold prices ------------------------------------------------------------------------------
/**
 * `GET /gold-prices`: the number our own site shows as "Dagens gullpris" — the published price per
 * gram, not what a customer is paid for it.
 */
export const publicGoldPricesSchema = z.object({
    currency: z.literal("NOK"),
    updatedAt: isoDateTimeSchema
        .nullable()
        .describe("When the price was fetched or typed in; null when we hold no price"),
    prices: z
        .array(z.object({
        karat: z.union([z.literal(24), z.literal(22), z.literal(18), z.literal(14), z.literal(9)]),
        pricePerGram: nokAmountSchema.describe("Kroner per gram"),
    }))
        .describe("Karat descending; empty when we hold no price"),
});
// ---- Pages ------------------------------------------------------------------------------------
/** Pass back as `?cursor=` for the next page. `null` on the last page, so a full last page still ends here. */
const nextCursorSchema = cursorSchema.nullable();
export const publicBookingsPageSchema = z.object({
    bookings: z.array(publicBookingSchema).describe("Newest startTime first"),
    nextCursor: nextCursorSchema,
});
export const publicGoldDeliveriesPageSchema = z.object({
    goldDeliveries: z.array(publicGoldDeliverySchema).describe("Newest createdAt first"),
    nextCursor: nextCursorSchema,
});
export const publicPayoutsPageSchema = z.object({
    payouts: z.array(publicPayoutSchema).describe("Newest first, by when the payout was created"),
    nextCursor: nextCursorSchema,
});
// ---- Envelope ---------------------------------------------------------------------------------
/**
 * Every failure, with the HTTP status it travels with. `code` is what TRY branches on;
 * `message` is Norwegian and written for our logs, not for a customer to read.
 */
export const PUBLIC_ERRORS = {
    API_KEY_REQUIRED: { status: 401, when: "No Authorization: Bearer header" },
    API_KEY_INVALID: { status: 401, when: "The key matches none of ours" },
    API_NOT_CONFIGURED: {
        status: 503,
        when: "No key is configured on our side, so nobody is admitted",
    },
    VALIDATION: {
        status: 400,
        when: "customerId is nothing but spaces, limit is outside 1–100, or cursor is not one we issued",
    },
    NOT_FOUND: { status: 404, when: "No such route under /api/public" },
    INTERNAL: { status: 500, when: "Our side failed. Retry later; tell us if it persists" },
};
export const publicErrorCodeSchema = z.enum(Object.keys(PUBLIC_ERRORS));
export const publicErrorSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: publicErrorCodeSchema,
        message: z.string(),
        details: z.unknown().optional().describe("On VALIDATION: which field, and why"),
    }),
});
function publicResponse(page) {
    return z.discriminatedUnion("success", [
        z.object({ success: z.literal(true), data: page }),
        publicErrorSchema,
    ]);
}
/** `GET /bookings/{customerId}` */
export const publicBookingsResponseSchema = publicResponse(publicBookingsPageSchema);
/** `GET /gold-deliveries/{customerId}` */
export const publicGoldDeliveriesResponseSchema = publicResponse(publicGoldDeliveriesPageSchema);
/** `GET /payouts/{customerId}` */
export const publicPayoutsResponseSchema = publicResponse(publicPayoutsPageSchema);
/** `GET /gold-prices` */
export const publicGoldPricesResponseSchema = publicResponse(publicGoldPricesSchema);
